import { motion } from "framer-motion";
import {
  ArrowDown,
  Mail,
  Github,
  Linkedin,
  Twitter,
  Youtube,
  Download,
  Eye,
  Code2,
  Globe,
  Lightbulb,
  Share2,
  Copy,
  Check,
  Brain,
  Settings2,
  PenLine,
  Bot,
} from "lucide-react";
import { useState } from "react";

const stats = [
  { value: "32+", label: "Projects Shipped" },
  { value: "4+", label: "Years Building" },
  { value: "10+", label: "Happy Clients" },
  { value: "5+", label: "Live Products" },
];

const HeroSection = () => {
  const scrollTo = (id) => {
    if (!id) return;
    const el = document.getElementById(id);
    if (!el) return;
    const offset = document.querySelector("header")?.offsetHeight || 0;
    window.scrollTo({
      top: el.getBoundingClientRect().top + window.pageYOffset - offset,
      behavior: "smooth",
    });
  };

  const CV_URL = "/docs/Boye_Emmanuel_Tete_CV.pdf";
  const [cvCopied, setCvCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const shareCV = async () => {
    const fullUrl = window.location.origin + CV_URL;
    if (navigator.share) {
      await navigator.share({ title: "Boye Emmanuel Tete — CV", url: fullUrl });
    } else {
      await navigator.clipboard.writeText(fullUrl);
      setCvCopied(true);
      setTimeout(() => setCvCopied(false), 2000);
    }
  };

  const copyLink = async () => {
    const fullUrl = window.location.origin + CV_URL;
    await navigator.clipboard.writeText(fullUrl);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gray-50 dark:bg-[#080d14]">
      {/* Background — subtle grid + orbs */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage: `linear-gradient(rgba(16,185,129,0.06) 1px, transparent 1px),
                             linear-gradient(90deg, rgba(16,185,129,0.06) 1px, transparent 1px)`,
            backgroundSize: "64px 64px",
          }}
        />
        <motion.div
          className="absolute -top-32 -left-32 w-[480px] h-[480px] bg-emerald-500/10 rounded-full blur-[120px]"
          animate={{ scale: [1, 1.15, 1], opacity: [0.4, 0.6, 0.4] }}
          transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute -bottom-40 -right-40 w-[560px] h-[560px] bg-teal-500/10 rounded-full blur-[140px]"
          animate={{ scale: [1.1, 1, 1.1], opacity: [0.5, 0.3, 0.5] }}
          transition={{ duration: 11, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] bg-cyan-500/5 rounded-full blur-[100px]"
          animate={{ scale: [1, 1.3, 1], opacity: [0.2, 0.35, 0.2] }}
          transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      <div className="relative z-10 w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="text-center space-y-8">

          {/* Name & headline */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.7 }}
            className="space-y-3"
          >
            <p className="text-gray-400 dark:text-gray-500 text-lg font-medium tracking-widest uppercase">
              Hi, I'm
            </p>
            <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-extrabold leading-[1.05] tracking-tight text-gray-900 dark:text-white">
              Boye{" "}
              <span className="text-emerald-400">
                Emmanuel Tete
              </span>
            </h1>
          </motion.div>

          {/* Role badges */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.28, duration: 0.6 }}
            className="flex flex-wrap justify-center gap-3"
          >
            {[
              { icon: Code2, label: "Software/AI Engineer" },

{ icon: Lightbulb, label: "Creative Director" },
              { icon: Bot, label: "STEM Robotics Instructor" },
              { icon: PenLine, label: "Blogger" },
            ].map(({ icon: Icon, label }) => (
              <span
                key={label}
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 text-sm font-medium"
              >
                <Icon size={14} className="text-emerald-400" />
                {label}
              </span>
            ))}
          </motion.div>

          {/* CTA buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.58, duration: 0.6 }}
            className="flex flex-wrap justify-center gap-4 pt-2"
          >
            <button
              onClick={() => scrollTo("contact")}
              className="group relative inline-flex items-center gap-2.5 px-8 py-4 bg-emerald-500 text-white font-bold rounded-xl overflow-hidden shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40 transition-all duration-300 hover:-translate-y-0.5"
            >
              <div className="absolute inset-0 bg-teal-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <span className="relative z-10 flex items-center gap-2.5">
                <Mail size={18} />
                Hire Me. Reply in 24h
              </span>
            </button>

            <a
              href={CV_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2.5 px-8 py-4 border border-gray-200 dark:border-white/15 bg-white dark:bg-white/5 hover:bg-gray-50 dark:hover:bg-white/10 text-gray-900 dark:text-white font-bold rounded-xl backdrop-blur-sm transition-all duration-300 hover:-translate-y-0.5"
            >
              <Eye size={18} className="text-emerald-400" />
              View CV
            </a>

            <a
              href={CV_URL}
              download="Boye_Emmanuel_Tete_CV.pdf"
              className="inline-flex items-center gap-2.5 px-5 py-4 border border-gray-200 dark:border-white/15 bg-white dark:bg-white/5 hover:bg-gray-50 dark:hover:bg-white/10 text-gray-900 dark:text-white font-bold rounded-xl backdrop-blur-sm transition-all duration-300 hover:-translate-y-0.5"
            >
              <Download size={18} className="text-emerald-400" />
              Download
            </a>

            <button
              onClick={shareCV}
              className="inline-flex items-center gap-2.5 px-5 py-4 border border-gray-200 dark:border-white/10 bg-white dark:bg-white/4 hover:bg-gray-50 dark:hover:bg-white/8 text-gray-500 dark:text-gray-400 hover:text-emerald-500 dark:hover:text-emerald-400 rounded-xl backdrop-blur-sm transition-all duration-300 hover:-translate-y-0.5 text-sm"
            >
              <Share2 size={16} />
              {cvCopied ? "Copied!" : "Share CV"}
            </button>

            <button
              onClick={copyLink}
              className="inline-flex items-center gap-2 px-5 py-4 border border-gray-200 dark:border-white/10 bg-white dark:bg-white/4 hover:bg-gray-50 dark:hover:bg-white/8 text-gray-500 dark:text-gray-400 hover:text-emerald-500 dark:hover:text-emerald-400 rounded-xl backdrop-blur-sm transition-all duration-300 hover:-translate-y-0.5 text-sm"
            >
              {linkCopied ? <Check size={16} className="text-emerald-500" /> : <Copy size={16} />}
              {linkCopied ? "Link Copied!" : "Copy Link"}
            </button>
          </motion.div>

          {/* Social links */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.68, duration: 0.5 }}
            className="flex justify-center gap-3 pt-2"
          >
            {[
              { href: "https://github.com/t-boye", icon: Github, label: "GitHub" },
              { href: "https://linkedin.com/in/tboyetech", icon: Linkedin, label: "LinkedIn" },
              { href: "https://www.youtube.com/@tboyex", icon: Youtube, label: "YouTube" },
              { href: "https://twitter.com/tboyetech", icon: Twitter, label: "Twitter" },
            ].map(({ href, icon: Icon, label }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                className="w-11 h-11 flex items-center justify-center rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 hover:bg-emerald-50 dark:hover:bg-emerald-500/15 hover:border-emerald-400 dark:hover:border-emerald-500/40 text-gray-500 dark:text-gray-400 hover:text-emerald-500 dark:hover:text-emerald-400 transition-all duration-200"
              >
                <Icon size={18} />
              </a>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.button
        onClick={() => scrollTo("about")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5 text-gray-400 dark:text-gray-600 hover:text-emerald-500 dark:hover:text-emerald-400 transition-colors cursor-pointer group"
      >
        <span className="text-xs tracking-widest uppercase group-hover:text-emerald-400 transition-colors">
          Explore
        </span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity }}
        >
          <ArrowDown size={20} />
        </motion.div>
      </motion.button>
    </section>
  );
};

export default HeroSection;
